import { Link } from "react-router-dom";
import { Video, Rotate3d, Music2, BookOpen, ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const DIM_ICONS = [Video, Rotate3d, Music2, BookOpen];
const DIM_NUMERALS = ["I", "II", "III", "IV"];

const ProgettoPage = () => {
  const { t } = useLang();
  useSeo("progetto");
  const formula = t("progetto.formula");
  const dims = t("progetto.dims");
  const evo = t("progetto.evo");

  return (
    <div data-testid="progetto-page">
      <PageHero
        testId="progetto-hero"
        image="/assets/editorial-village.webp"
        eyebrow={t("progetto.eyebrow")}
        lines={[t("progetto.titleA"), t("progetto.titleB")]}
        subtitle={t("progetto.heroSub")}
      />

      {/* FORMULA */}
      <section className="py-20 lg:py-28 border-b border-[#1C1B18]/10" data-testid="progetto-formula">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.35em] uppercase text-[#C5A059] font-medium">{t("progetto.formulaEyebrow")}</p>
            <p className="font-serif-display text-2xl sm:text-3xl lg:text-4xl mt-8 leading-snug">
              {formula.map((part, i) => (
                <span key={part}>
                  {i > 0 && <span className="italic text-[#9E7B36]"> + </span>}
                  {part}
                </span>
              ))}
            </p>
            <p className="text-sm text-[#666157] mt-6 max-w-2xl mx-auto leading-relaxed">
              {t("progetto.formulaNote")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* QUATTRO DIMENSIONI */}
      <section className="py-24 lg:py-32" data-testid="progetto-dimensions">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <SectionHead eyebrow={t("progetto.dimEyebrow")} title={t("progetto.dimTitle")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mt-16">
            {dims.map((d, i) => {
              const Icon = DIM_ICONS[i];
              return (
                <Reveal key={d.t} delay={0.08 * i}>
                  <div className="border-t border-[#C5A059]/40 pt-7 group" data-testid={`dimension-${i}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="font-serif-display italic text-3xl text-[#C5A059]/50 group-hover:text-[#9E7B36] transition-colors duration-500">{DIM_NUMERALS[i]}</span>
                      <Icon size={20} strokeWidth={1.4} className="text-[#9E7B36]" />
                    </div>
                    <h3 className="font-serif-display text-2xl mt-5">{d.t}</h3>
                    <p className="text-sm text-[#666157] mt-3 leading-relaxed">{d.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* EVOLUZIONE */}
      <section className="py-24 lg:py-32 bg-[#121212] text-[#F7F5F0]" data-testid="progetto-evolution">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <SectionHead dark eyebrow={t("progetto.evoEyebrow")} title={t("progetto.evoTitle")} />
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-3xl text-base sm:text-lg leading-relaxed text-[#F7F5F0]/70">
              {t("progetto.evoBody")}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-16">
            {evo.map((e, i) => (
              <Reveal key={e.tag} delay={0.1 * i}>
                <div className="border border-[#C5A059]/25 p-8 h-full" data-testid={`evolution-${i}`}>
                  <h3 className="text-[12px] tracking-[0.3em] uppercase font-semibold text-[#C5A059]">{e.tag}</h3>
                  <ul className="mt-6 space-y-3">
                    {e.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-[#F7F5F0]/75 leading-relaxed">
                        <span className="block w-1 h-1 bg-[#C5A059] rotate-45 mt-2 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Link
              to="/per-comuni-e-partner"
              data-testid="progetto-partner-cta"
              className="mt-14 inline-flex items-center gap-3 border border-[#C5A059]/60 px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A059] hover:text-[#121212] transition-colors duration-500"
            >
              {t("progetto.cta")} <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ProgettoPage;
